/**
 * Quick capture host state (design `api.openCapture` / `closeCapture`): any screen can open the
 * «Зафиксировать» dialog, optionally with a preset (event type and fields, e.g. from Состав).
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useCan } from '../../lib/session';

export type CaptureEventType = 'join' | 'leave' | 'project' | 'rate' | 'person' | 'status' | 'payment' | 'note' | 'employ' | 'edit';

/** Prefilled fields; keys follow the capture form (person, project, from, assignment, …). */
export interface CapturePreset {
  readonly type?: CaptureEventType;
  readonly [field: string]: unknown;
}

export interface CaptureState {
  /** Null while closed. */
  readonly preset: CapturePreset | null;
  /** Bumps on every open, so the form re-initialises even with the same preset. */
  readonly seq: number;
}

interface CaptureApi {
  /** «Зафиксировать» needs `capture.write` (decisions-log W9); without it nothing opens. */
  readonly isAvailable: boolean;
  readonly state: CaptureState;
  readonly open: (preset?: CapturePreset) => void;
  readonly close: () => void;
}

const CaptureContext = createContext<CaptureApi>({
  isAvailable: false,
  state: { preset: null, seq: 0 },
  open: () => undefined,
  close: () => undefined,
});

export function CaptureProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<CaptureState>({ preset: null, seq: 0 });
  const isAvailable = useCan()('capture.write');
  const open = useCallback(
    (preset?: CapturePreset) => {
      if (isAvailable) setState((current) => ({ preset: preset ?? {}, seq: current.seq + 1 }));
    },
    [isAvailable],
  );
  const close = useCallback(() => setState((current) => ({ ...current, preset: null })), []);
  const value = useMemo(() => ({ isAvailable, state, open, close }), [isAvailable, state, open, close]);
  return <CaptureContext.Provider value={value}>{children}</CaptureContext.Provider>;
}

export function useCapture(): CaptureApi {
  return useContext(CaptureContext);
}
